// Manifest helpers — every widget exports a manifest describing
// what proofs it can render. The picker uses this to show
// "X of Y proofs work here" so users avoid choosing a widget
// that has nothing to display.
//
// Manifest shape:
//   {
//     key: "Stagger",                 // matches Widget#kind in DB
//     name: "Stagger",                // user-facing name
//     description: "...",
//     thumb: "...",                   // optional preview img
//     kinds: ["text", "video"],       // proof.kind values accepted
//     requires: ["avatar"],           // proof fields that must be present
//     prefers: ["rating"],            // optional fields rendered when present
//     minProofs: 3,                   // minimum number of compatible proofs
//     tier: 1,                        // ordering hint in the picker
//   }

// The 27 widget manifests. They used to sit beside their components in
// public/*.jsx, which meant importing the metadata dragged the component in
// with it: the kind picker only ever wants names and counts, but pulled all
// 27 components into the bundle, and so did every embedded widget. They live
// here so the metadata stays cheap to import on its own and the components
// can be loaded one at a time (see registry.jsx).
//
// `key` matches Widget#kind in the database. Ruby mirrors kinds and
// maxProofs in Widget::KIND_MANIFESTS so the server can inline a widget's
// first page of proofs; test/models/widget_manifest_sync_test.rb parses THIS
// file and fails when the two drift apart.
//
// Array order is the kind picker's display order.
export const MANIFESTS = [
  // Masonry.jsx
  {
    key: "Masonry",
    name: "Wall of Love",
    description: "Pinterest-style masonry wall with mixed text, video, and audio cards.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar", "rating"],
    minProofs: 3,
    maxProofs: 50,
    supportsInfiniteScroll: true,
    tier: 1,
    category: "wall",
  },
  // QuoteGrid.jsx
  {
    key: "Quote Grid",
    name: "Quote Grid",
    description: "Minimal text-only grid — pure quotes in serif type with attribution.",
    kinds: ["text"],
    requires: [],
    prefers: [],
    minProofs: 3,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 1,
    category: "wall",
  },
  // ImageGallery.jsx
  {
    key: "Image Gallery",
    name: "Image Gallery",
    description: "Masonry of imported testimonial screenshots — tweets, LinkedIn posts, email screengrabs.",
    kinds: ["text", "video", "audio"],
    requires: ["screenshot_url"],
    prefers: [],
    minProofs: 3,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 3,
    category: "wall",
  },
  // Marquee.jsx
  {
    key: "Scrolling Wall",
    name: "Marquee",
    description: "Three rows of cards scrolling in alternating directions, infinite loop.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar", "rating"],
    minProofs: 4,
    maxProofs: 30,
    supportsInfiniteScroll: false,
    tier: 1,
    category: "marquee",
  },
  // Highlights.jsx
  {
    key: "Highlights",
    name: "Highlights",
    description: "Horizontal scrolling strip of testimonial soundbites in display type.",
    kinds: ["text"],
    requires: [],
    prefers: [],
    minProofs: 3,
    maxProofs: 50,
    supportsInfiniteScroll: false,
    tier: 3,
    category: "marquee",
  },
  // Carousel.jsx
  {
    key: "Carousel",
    name: "Carousel",
    description: "One proof at a time, navigate with arrows or dots.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar", "rating"],
    minProofs: 1,
    maxProofs: 20,
    supportsInfiniteScroll: false,
    tier: 1,
    category: "carousel",
  },
  // SlabCarousel.jsx
  {
    key: "Slab",
    name: "Slab Carousel",
    description: "Full-bleed billboard-style slides — one testimonial fills the canvas.",
    kinds: ["text", "video"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 12,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "carousel",
  },
  // HeroQuote.jsx
  {
    key: "Hero Quote",
    name: "Hero Quote",
    description:
      "One enormous pull-quote, billboard-style — perfect for above-the-fold marketing hero blocks.",
    kinds: ["text"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 1,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "hero",
  },
  // HeroVideo.jsx
  {
    key: "Hero Video",
    name: "Hero Video",
    description:
      "Single full-bleed video that autoplays muted — a video testimonial as a marketing hero.",
    kinds: ["video"],
    requires: ["video_url"],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 1,
    supportsInfiniteScroll: false,
    tier: 3,
    category: "hero",
  },
  // Stories.jsx
  {
    key: "Stories",
    name: "Stories",
    description: "Vertical 9:16 video player with tap-to-advance — TikTok / Instagram Stories style.",
    kinds: ["video"],
    requires: ["video_url"],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 20,
    supportsInfiniteScroll: false,
    tier: 4,
    category: "hero",
  },
  // TweetGrid.jsx
  {
    key: "Tweet Grid",
    name: "Tweet Grid",
    description: "Twitter/X card replicas — perfect for showcasing tweet-style social proof.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["source_platform"],
    minProofs: 2,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 3,
    category: "wall",
  },
  // VideoReels.jsx
  {
    key: "Video Reels",
    name: "Video Reels",
    description: "TikTok-style portrait video tiles in a horizontal row, hover-to-play.",
    kinds: ["video"],
    requires: ["video_url"],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 18,
    supportsInfiniteScroll: false,
    tier: 3,
    category: "wall",
  },
  // AudioWall.jsx
  {
    key: "Audio Wall",
    name: "Audio Wall",
    description: "Wall of audio testimonials with inline waveform players.",
    kinds: ["audio"],
    requires: ["audio_url"],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 24,
    supportsInfiniteScroll: true,
    tier: 3,
    category: "wall",
  },
  // List.jsx
  {
    key: "List",
    name: "List",
    description: "Single-column vertical list — one testimonial per row.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 3,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 1,
    category: "list",
  },
  // BubbleList.jsx
  {
    key: "Bubble List",
    name: "Bubble List",
    description: "Chat-style alternating bubbles — testimonials read like a thread.",
    kinds: ["text"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 3,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 3,
    category: "list",
  },
  // AvatarStrip.jsx
  {
    key: "Avatar Strip",
    name: "Avatar Strip",
    description: "Overlapping avatars with a '+N more' count.",
    kinds: ["text", "video", "audio"],
    requires: ["avatar"],
    prefers: ["rating"],
    minProofs: 3,
    maxProofs: 12,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "compact",
  },
  // AvatarStripPopup.jsx
  {
    key: "Avatar Strip Popup",
    name: "Avatar Strip Popup",
    description:
      "Overlapping avatars; click any avatar to pop open the full testimonial — text, video, or audio.",
    kinds: ["text", "video", "audio"],
    requires: ["avatar"],
    prefers: ["rating"],
    minProofs: 3,
    maxProofs: 12,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "compact",
  },
  // StatGrid.jsx
  {
    key: "Stat Grid",
    name: "Stat Grid",
    description: "Big-number stats auto-derived from your testimonial set.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["rating"],
    minProofs: 1,
    maxProofs: 6,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "stat",
  },
  // RatingBadge.jsx
  {
    key: "Rating Badge",
    name: "Rating Badge",
    description: "Compact inline pill — '4.8★ from 234 reviews' — sits in document flow next to a CTA.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["rating"],
    minProofs: 3,
    maxProofs: 50,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "compact",
  },
  // LogoBar.jsx
  {
    key: "Logo Bar",
    name: "Logo Bar",
    description: "Greyscale wall of customer logos — the classic 'trusted by' band.",
    kinds: ["text", "video", "audio"],
    requires: ["contact_company_logo"],
    prefers: [],
    minProofs: 3,
    maxProofs: 30,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "logo",
  },
  // FloatingBadge.jsx
  {
    key: "Floating Badge",
    name: "Floating Badge",
    description:
      "Compact rating badge that floats on a customer's page — '4.8★ from 234 reviews'.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["rating"],
    minProofs: 3,
    maxProofs: 5,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "floating",
  },
  // FloatingToast.jsx
  {
    key: "Floating Toast",
    name: "Floating Toast",
    description:
      "Bottom-corner popup that rotates through real testimonials every few seconds.",
    kinds: ["text", "video"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 2,
    maxProofs: 10,
    supportsInfiniteScroll: false,
    tier: 3,
    category: "floating",
  },
  // PressWall.jsx
  {
    key: "Press Wall",
    name: "Press Wall",
    description:
      "Press mentions and media coverage with publication logos and pull-quotes.",
    kinds: ["text"],
    requires: ["source_platform"],
    prefers: [],
    minProofs: 2,
    maxProofs: 30,
    supportsInfiniteScroll: true,
    tier: 4,
    category: "wall",
  },
  // TabbedWall.jsx
  {
    key: "Tabbed Wall",
    name: "Tabbed Wall",
    description:
      "Wall of love with filter chips at the top — visitors can browse by kind or rating.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar", "rating"],
    minProofs: 3,
    maxProofs: 50,
    supportsInfiniteScroll: true,
    tier: 4,
    category: "wall",
  },
  // NotionQuote.jsx
  {
    key: "Inline Quote",
    name: "Inline Quote",
    description: "A single quote callout for embedding in blog posts and case studies.",
    kinds: ["text"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 1,
    supportsInfiniteScroll: false,
    tier: 4,
    category: "inline",
  },
  // SingleProof.jsx
  {
    key: "Single Testimonial",
    name: "Single Testimonial",
    description: "Hand-picked single testimonial card with four layout presets — drop into a blog post or pricing page.",
    kinds: ["text", "video"],
    requires: [],
    prefers: ["avatar"],
    minProofs: 1,
    maxProofs: 1,
    supportsInfiniteScroll: false,
    tier: 3,
    category: "inline",
  },
  // Stagger.jsx
  {
    key: "Stagger",
    name: "Stagger",
    description: "Cards stacked at angles, rotating around the center.",
    kinds: ["text", "video", "audio"],
    requires: [],
    prefers: ["avatar", "rating"],
    minProofs: 3,
    maxProofs: 10,
    supportsInfiniteScroll: false,
    tier: 2,
    category: "carousel",
  },
];

export const findManifest = (kind) =>
  MANIFESTS.find((m) => m.key === kind) || null;

export const proofIsCompatible = (manifest, proof) => {
  if (!manifest.kinds.includes(proof.kind)) return false;
  for (const field of manifest.requires || []) {
    if (!proof[field]) return false;
  }
  return true;
};

export const filterProofs = (manifest, proofs) =>
  (proofs || []).filter((p) => proofIsCompatible(manifest, p));

export const compatibilityCount = (manifest, proofs) => {
  const list = proofs || [];
  return {
    compatible: list.filter((p) => proofIsCompatible(manifest, p)).length,
    total: list.length,
  };
};
